import { t } from 'i18next';

import { config } from '~/config';

import type { ConversationInventory } from '../services/inventory-service';
import { ConversationTask } from '../use-chat/use-chat.types';
import { VisualizationTable } from '../use-visualization-store/useVisualizationStore';
import { buildInventoryContent } from './buildInventoryContent';
import { getDisclaimer } from './getDisclaimer';
import { BODY_STYLE, htmlNodes } from './htmlNodes';
import { markdownParser } from './markdownParser';

export type CopyAction = 'copy' | 'print';

interface CopyResearchMessageArgs {
  task: ConversationTask;
  charts: Record<string, HTMLDivElement>;
  tables: Record<string, VisualizationTable>;
  inventory?: ConversationInventory;
  action: CopyAction;
  taskQuestion: string;
}

export const copyResearchMessage = async ({
  task,
  charts,
  tables,
  inventory,
  action,
  taskQuestion,
}: CopyResearchMessageArgs) => {
  if (task.states) {
    let { htmlContent, plainContent } = task.states.reduce(
      (result, curr) => {
        result.plainContent += `\n\n## ${curr.title}\n\n`;
        result.htmlContent += htmlNodes.h2(curr.title);

        const processedHtmlContent = markdownParser({
          content: curr.content,
          charts,
          tables,
        });

        result.plainContent += curr.content;
        result.plainContent +=
          '\n\n' + t('chat:disclaimer') + t('chat:disclaimerContent');
        result.htmlContent += processedHtmlContent + '\n\n';
        result.htmlContent +=
          `<p> ${t('chat:disclaimer')} ${t('chat:disclaimerContent')}</p>` +
          '\n';

        return result;
      },
      {
        htmlContent: '',
        plainContent: '',
      }
    );

    const { html: inventoryHtml, text: inventoryText } =
      buildInventoryContent({ inventory });

    plainContent += inventoryText;
    htmlContent += inventoryHtml;

    const { disclaimerHtml } = getDisclaimer();

    htmlContent += disclaimerHtml;

    if (action === 'print') {
      const brandNamePath = `${config.urls.root}/app/static/images/brand/logo-light.svg`;
      htmlContent = `<img src="${brandNamePath}" style="width: 103px; height: auto;" />${htmlContent}`;
    }

    const printDocument = `
  <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${taskQuestion}</title>
        <style>
          body { ${BODY_STYLE} }
          @page {
            @bottom-left {
              font-size: 8px;
              content: "Powered by Workspace AI";
            }
            @bottom-right {
              font-size: 8px;
              content: counter(page) "/" counter(pages);
            }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
  `;

    if (action === 'print') {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDoc) {
        const blob = new Blob([printDocument], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframe.src = url;

        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(iframe);
          }, 100);
        };
      }
    } else {
      const handleCopy = (event: ClipboardEvent) => {
        event.preventDefault();

        if (event.clipboardData) {
          event.clipboardData.clearData();
          event.clipboardData.setData('text/plain', plainContent.trim());
          event.clipboardData.setData('text/html', printDocument);
        }
      };

      document.addEventListener('copy', handleCopy);

      document.execCommand?.('copy');
      document.removeEventListener('copy', handleCopy);
    }
  }
};
